import {
    connect,
    JSONCodec,
    NatsConnection,
    Subscription,
    Msg,
    ConnectionOptions
} from 'nats.ws';

const jc = JSONCodec();

/**
 * Interfaces for the NATS Action Pattern
 */
export interface NatsPayload<T = any> {
    action: string;
    data: T;
}

export interface ActionHandlers {
    [actionName: string]: (data: any) => any;
}

/**
 * Connect to NATS server
 */
export const connectNats = async (
    servers: string | string[],
    user?: string,
    pass?: string
): Promise<NatsConnection> => {
    const opts: ConnectionOptions = { servers, user, pass };
    try {
        const natsConn = await connect(opts);
        return natsConn;
    } catch (err) {
        console.error('NATS Connection Error:', err);
        throw err;
    }
};

/**
 * Connect to NATS with automatic retry on cold start failures
 */
export const connectNatsWithRetry = async (
    servers: string | string[],
    user?: string,
    pass?: string,
    maxRetries = 3,
    delayMs = 2000
): Promise<NatsConnection> => {
    let lastErr: unknown;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await connectNats(servers, user, pass);
        } catch (err) {
            lastErr = err;
            console.warn(`NATS connection attempt ${attempt} failed:`, err);
            if (attempt < maxRetries) {
                await new Promise(r => setTimeout(r, delayMs));
            }
        }
    }
    throw lastErr;
};

/**
 * Subscribe to a subject (Standard Listener)
 */
export const natsSubscribe = (
    conn: NatsConnection,
    subject: string,
    actionsFuncObj: ActionHandlers
): Subscription => {
    return incomingNatsAction(conn, subject, actionsFuncObj, false);
};

/**
 * Subscribe and respond to NATS requests (RPC)
 */
export const natsReply = (
    conn: NatsConnection,
    subject: string,
    actionsFuncObj: ActionHandlers
): Subscription => {
    return incomingNatsAction(conn, subject, actionsFuncObj, true);
};

const incomingNatsAction = (
    conn: NatsConnection,
    subject: string,
    actionsFuncObj: ActionHandlers,
    isResponder: boolean
): Subscription => {
    return conn.subscribe(subject, {
        callback: (err, msg) => {
            if (err) {
                console.error(`Subscription error on ${subject}:`, err);
                return;
            }
            try {
                const { action, data } = verifyAndParse(msg, actionsFuncObj);
                const result = actionsFuncObj?.[action]?.(data);

                if (isResponder) {
                    // Check if result exists before responding
                    if (result === undefined || result === null) {
                        throw new Error(`Action "${action}" returned no result for responder.`);
                    }
                    msg.respond(jc.encode({ action, data: result }));
                }
            } catch (e) {
                console.error(`NATS Inbound error [${subject}]:`, e);
            }
        }
    });
};

/**
 * Send a request and expect a response (RPC)
 */
export const natsRequest = async <T = any>(
    conn: NatsConnection,
    subject: string,
    actionsFuncObj: ActionHandlers,
    action: string,
    data: any,
    parseResponseFunc?: (response: T) => void,
    timeout?: number
): Promise<void> => {
    return outgoingNatsAction(conn, subject, actionsFuncObj, action, data, true, parseResponseFunc, timeout);
};

/**
 * Fire and forget publish
 */
export const natsPublish = (
    conn: NatsConnection,
    subject: string,
    actionsFuncObj: ActionHandlers,
    action: string,
    data: any
): void => {
    void outgoingNatsAction(conn, subject, actionsFuncObj, action, data, false);
};

const outgoingNatsAction = async (
    conn: NatsConnection,
    subject: string,
    actionsFuncObj: ActionHandlers,
    action: string,
    data: any,
    isRequest: boolean,
    parseResponseFunc?: (response: any) => void,
    timeout?: number
): Promise<void> => {
    verifyAction(actionsFuncObj, action);
    const payload: NatsPayload = { action, data };
    const msg = jc.encode(payload);

    if (isRequest) {
        try {
            const response = await conn.request(subject, msg, { timeout: timeout || 5000 });
            if (parseResponseFunc) {
                const decoded = jc.decode(response.data) as NatsPayload;
                parseResponseFunc(decoded.data);
            }
        } catch (err) {
            console.error(`NATS Request failed on ${subject}:`, err);
            throw err;
        }
    } else {
        conn.publish(subject, msg);
    }
};

const verifyAndParse = (msg: Msg, actions: ActionHandlers): NatsPayload => {
    const jm = jc.decode(msg.data) as NatsPayload;
    verifyAction(actions, jm.action);
    return {
        action: jm.action,
        data: jm.data
    };
};

const verifyAction = (actions: ActionHandlers, action: string): void => {
    if (!action) {
        throw new Error('Missing proper message structure: No action specified.');
    }
    if (!(action in actions)) {
        throw new Error(`Invalid action: "${action}" is not registered in the provided handler map.`);
    }
};

/**
 * Legacy/Direct Publish helper
 */
export const publish = (
    nc: NatsConnection,
    allowedActions: ActionHandlers,
    channel: string,
    msg: NatsPayload
): void => {
    verifyAction(allowedActions, msg.action);
    nc.publish(channel, jc.encode(msg));
};